import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../config/axiosInstance';
import Snackbar from '../components/Snackbar';
import MenuButton from '../components/MenuButton';

interface TrainingVideo {
    id: string;
    title: string;
    description: string | null;
    youtubeUrl: string;
    category: string | null;
    isPublished: boolean;
    createdAt: string;
}

interface FormState {
    title: string;
    description: string;
    youtubeUrl: string;
    category: string;
    isPublished: boolean;
}

const EMPTY_FORM: FormState = {
    title: '',
    description: '',
    youtubeUrl: '',
    category: 'general',
    isPublished: true,
};

const CATEGORIES = ['general', 'running', 'swimming', 'cycling', 'strength'];

const CATEGORY_LABELS: Record<string, string> = {
    general: 'General',
    running: 'Running',
    swimming: 'Swimming',
    cycling: 'Cycling',
    strength: 'Strength',
};

const CATEGORY_BADGES: Record<string, string> = {
    general: 'bg-blue-50 text-blue-600',
    running: 'bg-blue-100 text-blue-700',
    swimming: 'bg-blue-200 text-blue-800',
    cycling: 'bg-blue-300 text-blue-900',
    strength: 'bg-blue-600 text-white',
};

const extractYouTubeId = (url: string): string | null => {
    const patterns = [
        /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
        /(?:youtu\.be\/)([^?\s]+)/,
        /(?:youtube\.com\/embed\/)([^?\s]+)/,
    ];
    for (const p of patterns) {
        const m = url.match(p);
        if (m) return m[1];
    }
    return null;
};

const formatDate = (value: string): string => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const TrainingLibraryAdmin: React.FC = () => {
    const { t } = useTranslation();
    const [videos, setVideos] = useState<TrainingVideo[]>([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editVideo, setEditVideo] = useState<TrainingVideo | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [formError, setFormError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    const fetchVideos = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/training-library/admin/all');
            setVideos(res.data);
        } catch {
            setSnackbar({ message: 'Failed to load videos.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideos();
    }, []);

    const filteredVideos = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        if (!query) return videos;
        return videos.filter((video) => {
            const text = `${video.title} ${video.description || ''} ${video.category || 'general'}`.toLowerCase();
            return text.includes(query);
        });
    }, [videos, searchTerm]);

    const openAdd = () => {
        setEditVideo(null);
        setForm(EMPTY_FORM);
        setFormError('');
        setModalOpen(true);
    };

    const openEdit = (v: TrainingVideo) => {
        setEditVideo(v);
        setForm({
            title: v.title,
            description: v.description ?? '',
            youtubeUrl: v.youtubeUrl,
            category: v.category ?? 'general',
            isPublished: v.isPublished,
        });
        setFormError('');
        setModalOpen(true);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError('');

        if (!form.title.trim()) {
            setFormError(t('trainingLibrary.titleRequired'));
            return;
        }
        if (!form.youtubeUrl.trim()) {
            setFormError(t('trainingLibrary.youtubeUrlRequired'));
            return;
        }
        if (!extractYouTubeId(form.youtubeUrl)) {
            setFormError(t('trainingLibrary.invalidYoutubeUrl'));
            return;
        }

        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description.trim() || undefined,
                youtubeUrl: form.youtubeUrl.trim(),
                category: form.category,
                isPublished: form.isPublished,
            };
            if (editVideo) {
                await axiosInstance.patch(`/training-library/admin/${editVideo.id}`, payload);
                setSnackbar({ message: t('trainingLibrary.videoUpdatedSuccess'), type: 'success' });
            } else {
                await axiosInstance.post('/training-library/admin', payload);
                setSnackbar({ message: t('trainingLibrary.videoAddedSuccess'), type: 'success' });
            }
            setModalOpen(false);
            fetchVideos();
        } catch {
            setFormError(t('trainingLibrary.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm(t('trainingLibrary.deleteConfirm'))) return;

        setDeletingId(id);
        try {
            await axiosInstance.delete(`/training-library/admin/${id}`);
            setVideos((prev) => prev.filter((v) => v.id !== id));
            setSnackbar({ message: t('trainingLibrary.videoDeleted'), type: 'success' });
        } catch {
            setSnackbar({ message: 'Failed to delete.', type: 'error' });
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="flex-1 flex flex-col min-w-0 bg-background">
            {snackbar && (
                <Snackbar
                    message={snackbar.message}
                    type={snackbar.type}
                    onClose={() => setSnackbar(null)}
                />
            )}

            <header className="h-24 bg-primary border-b border-cyan-600 flex items-center justify-between px-4 md:px-8 shadow-md z-20 sticky top-0 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-90"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                <div className="relative z-10 flex items-center gap-3 md:gap-6 w-full justify-between">
                    <MenuButton />
                    <div className="bg-white/10 backdrop-blur-md px-3 md:px-6 py-2 rounded-lg border border-white/20 min-w-0 mr-auto">
                        <h1 className="text-xl font-bold text-white flex items-center gap-3">
                            <i className="fa-solid fa-photo-film"></i>
                            {t('trainingLibrary.title')}
                        </h1>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="hidden lg:flex items-center relative">
                            <input
                                type="text"
                                placeholder={t('trainingLibrary.search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-4 pr-12 py-2.5 bg-white/20 border border-white/30 focus:bg-white focus:text-slate-800 placeholder-white/70 focus:placeholder-slate-400 rounded shadow-inner text-white w-64 transition-all outline-none"
                            />
                            <button className="absolute right-1 top-1 bottom-1 px-3 bg-white text-primary rounded font-medium text-sm hover:bg-slate-50 transition-colors">
                                {t('common.search')}
                            </button>
                        </div>

                        <button
                            onClick={openAdd}
                            className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-2 md:py-2.5 bg-white text-primary rounded font-bold hover:shadow-lg hover:bg-slate-50 transition-all shadow-md border-b-4 border-cyan-200 active:border-b-0 active:translate-y-1"
                        >
                            <i className="fa-solid fa-plus text-sm md:text-lg"></i>
                            <span className="text-[11px] md:text-sm">{t('trainingLibrary.addVideoButton')}</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile search — the header input is lg-only, so without this there
                is no way to search on a phone. */}
            <div className="lg:hidden px-3 pt-3 pb-1 bg-slate-50">
                <div className="relative">
                    <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                    <input
                        type="text"
                        placeholder={t('trainingLibrary.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 text-base bg-white border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 lg:p-6 relative bg-slate-50">
                <div className="max-w-[1600px] mx-auto h-full flex flex-col">
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 border-b border-blue-600 text-xs font-black text-white uppercase tracking-wider">
                            <div className="col-span-2">{t('trainingLibrary.table.thumbnail')}</div>
                            <div className="col-span-4">{t('trainingLibrary.table.title')}</div>
                            <div className="col-span-2">{t('trainingLibrary.table.category')}</div>
                            <div className="col-span-2">{t('trainingLibrary.table.status')}</div>
                            <div className="col-span-1">{t('trainingLibrary.table.date')}</div>
                            <div className="col-span-1 text-center">{t('common.actions')}</div>
                        </div>

                        {loading ? (
                            <div className="flex items-center justify-center py-20">
                                <div className="text-slate-400">
                                    <i className="fa-solid fa-spinner fa-spin text-3xl"></i>
                                </div>
                            </div>
                        ) : filteredVideos.length === 0 ? (
                            <div className="py-16 px-6 text-center">
                                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-cyan-100 text-cyan-700 mb-4">
                                    <i className="fa-solid fa-video text-xl"></i>
                                </div>
                                <h3 className="text-lg font-bold text-slate-800 mb-2">{t('trainingLibrary.noVideos')}</h3>
                                <p className="text-sm text-slate-500 mb-5">
                                    {videos.length === 0
                                        ? t('trainingLibrary.emptyDescription')
                                        : t('trainingLibrary.noResultsDescription')}
                                </p>
                                <button
                                    onClick={openAdd}
                                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-bold text-sm transition-all shadow-md"
                                >
                                    <i className="fa-solid fa-plus"></i>
                                    {t('trainingLibrary.addVideo')}
                                </button>
                            </div>
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {filteredVideos.map((video) => {
                                    const ytId = extractYouTubeId(video.youtubeUrl);
                                    const category = video.category ?? 'general';
                                    const categoryLabel = CATEGORY_LABELS[category] ?? category;
                                    const categoryBadge = CATEGORY_BADGES[category] ?? CATEGORY_BADGES.general;

                                    return (
                                        <div key={video.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-4 hover:bg-slate-50 transition-colors">
                                            <div className="md:col-span-2">
                                                {ytId ? (
                                                    <img
                                                        src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                                                        alt={video.title}
                                                        className="w-28 h-16 object-cover rounded-lg border border-slate-200"
                                                    />
                                                ) : (
                                                    <div className="w-28 h-16 bg-slate-100 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400">
                                                        <i className="fa-solid fa-video"></i>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="md:col-span-4">
                                                <p className="font-semibold text-slate-800 line-clamp-2">{video.title}</p>
                                                {video.description && (
                                                    <p className="text-xs text-slate-500 mt-1 line-clamp-1">{video.description}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${categoryBadge}`}>
                                                    {t(`trainingLibrary.category.${category}`, categoryLabel)}
                                                </span>
                                            </div>

                                            <div className="md:col-span-2">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                    video.isPublished ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                    {video.isPublished ? t('trainingLibrary.statusPublished') : t('trainingLibrary.statusDraft')}
                                                </span>
                                            </div>

                                            <div className="md:col-span-1 text-sm text-slate-600">
                                                {formatDate(video.createdAt)}
                                            </div>

                                            <div className="md:col-span-1">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => openEdit(video)}
                                                        className="w-8 h-8 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center"
                                                        title={t('common.edit')}
                                                    >
                                                        <i className="fa-solid fa-pen-to-square text-xs"></i>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(video.id)}
                                                        disabled={deletingId === video.id}
                                                        className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center disabled:opacity-40"
                                                        title={t('common.delete')}
                                                    >
                                                        {deletingId === video.id
                                                            ? <i className="fa-solid fa-spinner fa-spin text-xs"></i>
                                                            : <i className="fa-solid fa-trash-can text-xs"></i>
                                                        }
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {filteredVideos.length > 0 && (
                            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
                                <div className="text-sm text-slate-500">
                                    {t('trainingLibrary.showingResults', { shown: filteredVideos.length, total: videos.length })}
                                </div>
                                <div className="text-sm text-slate-500">
                                    {t('trainingLibrary.publishedCount', { count: videos.filter((v) => v.isPublished).length })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {modalOpen && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setModalOpen(false);
                    }}
                >
                    <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full border border-slate-200 overflow-hidden flex flex-col max-h-[90dvh]">
                        <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                                    <i className="fa-solid fa-circle-play text-white text-lg"></i>
                                </div>
                                <h3 className="text-xl font-bold text-white">
                                    {editVideo ? t('trainingLibrary.editVideo') : t('trainingLibrary.addNewVideo')}
                                </h3>
                            </div>
                            <button
                                onClick={() => setModalOpen(false)}
                                className="w-8 h-8 rounded-lg hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                            >
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>

                        <form onSubmit={handleSave} className="p-4 md:p-6 space-y-3 md:space-y-4 bg-slate-50 overflow-y-auto">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('trainingLibrary.titleLabel')} <span className="text-cyan-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.title}
                                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                    placeholder={t('trainingLibrary.titlePlaceholder')}
                                    className="w-full px-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">
                                    {t('trainingLibrary.youtubeUrlLabel')} <span className="text-cyan-500">*</span>
                                </label>
                                <input
                                    type="url"
                                    value={form.youtubeUrl}
                                    onChange={(e) => setForm((f) => ({ ...f, youtubeUrl: e.target.value }))}
                                    placeholder={t('trainingLibrary.youtubeUrlPlaceholder')}
                                    className="w-full px-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                />
                                {form.youtubeUrl && extractYouTubeId(form.youtubeUrl) && (
                                    <img
                                        src={`https://img.youtube.com/vi/${extractYouTubeId(form.youtubeUrl)}/mqdefault.jpg`}
                                        alt={t('trainingLibrary.preview')}
                                        className="mt-1 w-44 h-24 object-cover rounded-lg border border-cyan-200"
                                    />
                                )}
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">{t('trainingLibrary.categoryLabel')}</label>
                                    <select
                                        value={form.category}
                                        onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                                        className="w-full px-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    >
                                        {CATEGORIES.map((c) => (
                                            <option key={c} value={c}>{t(`trainingLibrary.category.${c}`, CATEGORY_LABELS[c])}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">{t('trainingLibrary.statusLabel')}</label>
                                    <select
                                        value={form.isPublished ? 'published' : 'draft'}
                                        onChange={(e) => setForm((f) => ({ ...f, isPublished: e.target.value === 'published' }))}
                                        className="w-full px-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                                    >
                                        <option value="published">{t('trainingLibrary.statusPublished')}</option>
                                        <option value="draft">{t('trainingLibrary.statusDraft')}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-cyan-700 uppercase tracking-wide">{t('trainingLibrary.descriptionLabel')}</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                    placeholder={t('trainingLibrary.descriptionPlaceholder')}
                                    rows={3}
                                    className="w-full px-4 py-3 bg-white border border-cyan-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                                />
                            </div>

                            {formError && (
                                <div className="flex items-center gap-2 text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-sm">
                                    <i className="fa-solid fa-circle-exclamation"></i>
                                    <span>{formError}</span>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 pt-1">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    disabled={saving}
                                    className="px-5 py-2 rounded-lg text-sm font-medium text-cyan-700 bg-cyan-50 hover:bg-cyan-100 border border-cyan-200 transition-colors"
                                >
                                    {t('common.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-5 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white transition-colors flex items-center gap-2 disabled:opacity-60"
                                >
                                    {saving
                                        ? <><i className="fa-solid fa-spinner fa-spin"></i> {t('trainingLibrary.saving')}</>
                                        : editVideo
                                            ? <><i className="fa-solid fa-pen"></i> {t('trainingLibrary.update')}</>
                                            : <><i className="fa-solid fa-floppy-disk"></i> {t('trainingLibrary.save')}</>
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrainingLibraryAdmin;
